import { useCallback, useEffect, useReducer, useRef, useState, useSyncExternalStore } from 'react';
import {
  FeaturedBeachesResponse,
  FEATURED_ROUTE,
  applyFreshFeatured,
  getFeaturedBeaches,
  readCurrentRanking,
  subscribeRanking,
} from '../../services/api';
import {
  normalizeInstant,
  RANKING_REVALIDATE_AGE_MS,
  STALE_RANKING_THRESHOLD_MS,
} from '../provenance/provenance';
import { useServiceWorkerRefresh } from '../../hooks/useServiceWorkerRefresh';
import { useRevalidateOnReturn } from '../../hooks/useRevalidateOnReturn';

/**
 * The ranking in force, for every screen that paints a sky.
 *
 * There are five of them — home, map, list, detail and the landings — and each
 * one used to wire the same three things by hand: the request, the message the
 * service worker sends when the response it gave up on finally lands, and its
 * own copy of the answer. Only the home page also knew what "this is old"
 * means, so the other four painted last night's sky with nothing to say about
 * it, and the rule for how old is too old lived in the page that happened to
 * need it first.
 *
 * All of that is one module now. What a screen sees is the ranking, when it
 * was assembled, whether it is a copy from an earlier visit, and a way to ask
 * again. Everything else — the module cache, the worker's message, revalidating
 * when the tab comes back, the staleness threshold — is behind the seam.
 *
 * The ranking is READ from `services/api`, never copied into this hook: Ionic
 * keeps visited pages mounted, so a copy per screen would let the one that
 * fired the request repaint while the other four kept the sky they had loaded,
 * which is the very split this module exists to prevent.
 */
export interface RankingInUse {
  /** The ranking currently in force, or null until the first answer lands. */
  ranking: FeaturedBeachesResponse | null;
  /** When the backend ASSEMBLED it (epoch ms), null if it did not say. */
  updatedMs: number | null;
  /**
   * The painted ranking is a stored copy: older than anything `/featured` can
   * still be serving, so it came from the service worker or the snapshot.
   * Nothing the user does retires it — only a real answer does.
   */
  fromPreviousVisit: boolean;
  /** No answer yet, of any kind. */
  loading: boolean;
  /**
   * A copy from an earlier visit is being held back while the real answer is
   * on its way (`HOLD_PREVIOUS_VISIT_MS`): `ranking` is null and `loading` true
   * meanwhile, so screens can say "updating" instead of "searching".
   */
  refreshing: boolean;
  /** The request failed and there is nothing to paint. */
  error: boolean;
  /** A retry is in flight. */
  retrying: boolean;
  /** Ask the backend again, ignoring every cached copy. */
  retry: () => void;
}

/** How often a ranking past its revalidation age is asked for again. */
const AUTO_RETRY_EVERY_MS = 60 * 1000;

/**
 * When the last automatic refetch went out.
 *
 * Module-level and not a ref per screen, because every mounted screen would
 * otherwise send its own request for the same old body, and each attempt wakes
 * a sleeping Render instance. What the attempt brings back reaches every
 * screen, so whichever one sends it, all of them are served.
 */
let lastAutoRetryMs = 0;

/**
 * How long a copy from an earlier visit is kept OFF the screen while a real
 * answer may still arrive.
 *
 * Opening the app at night painted the afternoon's sky with "actualizado hace
 * 5h" next to it, and only then corrected itself: the service worker gives up
 * on the network at three seconds and hands over its stored copy, while the
 * backend is still waking up. Showing "updating" for a few seconds is honest;
 * showing hours-old conditions as the page is not. Past this wait the copy is
 * painted after all, with its notice — the backend may really be down, and an
 * old sky with a warning beats an endless spinner. Long enough for a Render
 * cold start to answer most of the time; a slower one still repaints when the
 * worker's late message lands.
 */
const HOLD_PREVIOUS_VISIT_MS = 30 * 1000;

/**
 * Which copy is being held, and since when. Module-level so every mounted
 * screen holds the same copy for the same wait instead of restarting it.
 */
let held: { updatedMs: number; sinceMs: number } | null = null;

export function useRanking(): RankingInUse {
  const ranking = useSyncExternalStore(
    subscribeRanking,
    readCurrentRanking,
    readCurrentRanking,
  );
  const [error, setError] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [, repaint] = useReducer((n: number) => n + 1, 0);

  // Only guards the two flags above. The ranking needs no such guard — it is
  // shared state and every screen wants it — but a screen the user navigated
  // away from must not be told its own request failed.
  const mountedFlag = useRef(true);
  useEffect(() => {
    mountedFlag.current = true;
    return () => { mountedFlag.current = false; };
  }, []);

  useEffect(() => {
    getFeaturedBeaches().catch(() => { if (mountedFlag.current) setError(true); });
  }, []);

  // The answer the service worker had given up on, arriving late. What gets
  // painted is whatever ends up IN FORCE, not what the message carried: if a
  // request in flight had already delivered a later ranking, this body is the
  // one being discarded. And never a refetch from here — a request writes the
  // cache, and writing the cache is what emits this message.
  useServiceWorkerRefresh(({ url, datos: data }) => {
    if (!url.endsWith(FEATURED_ROUTE)) return;
    const fresh = data as FeaturedBeachesResponse;
    // A body that is not a ranking would poison the cache for every screen.
    if (!Array.isArray(fresh.resumenTodas)) return;
    applyFreshFeatured(fresh);
  });

  // A page left open for twenty minutes keeps painting what it loaded then.
  // This does not force anything: while the module cache is fresh it answers
  // without asking the backend. The repaint re-judges the age right away, so a
  // ranking that went stale while the tab slept is held back before it shows.
  useRevalidateOnReturn(
    useCallback(() => {
      repaint();
      getFeaturedBeaches().catch(() => { /* what is painted still stands */ });
    }, []),
  );

  // `force`: what is painted came from a stored copy, so the module cache holds
  // that same body and a plain call would hand it back without asking anyone.
  const retry = useCallback(() => {
    setRetrying(true);
    return getFeaturedBeaches({ force: true })
      .then(() => { if (mountedFlag.current) setError(false); })
      // Nothing on failure, on purpose: a failed retry after a failed load
      // leaves the error already standing, and after a successful one what is
      // painted still stands, so there is nothing new to say.
      .catch(() => { /* we carry on with what is already painted */ })
      .finally(() => { if (mountedFlag.current) setRetrying(false); });
  }, []);

  const updatedMs = ranking ? normalizeInstant(ranking.timestamp) : null;
  const ageMs = updatedMs == null ? null : Date.now() - updatedMs;

  // When to ASK AGAIN and when to TELL THE USER are two different questions, and
  // they were one constant. The notice fired at ten minutes, which the backend
  // answers from its stale window all day long: it lit up on ordinary days and
  // no tap could retire it, because the same body was coming back. Asking again
  // at ten minutes is right — that IS when there is something newer to get.
  const shouldRevalidate = ageMs != null && ageMs > RANKING_REVALIDATE_AGE_MS;
  const fromPreviousVisit = ageMs != null && ageMs > STALE_RANKING_THRESHOLD_MS;

  // Offline there is no answer coming: the stored copy is all there is, at once.
  if (fromPreviousVisit && held?.updatedMs !== updatedMs && navigator.onLine !== false) {
    held = { updatedMs: updatedMs as number, sinceMs: Date.now() };
  }
  // A failed request ends the wait too: nothing better is on its way.
  const holding =
    fromPreviousVisit &&
    !error &&
    held?.updatedMs === updatedMs &&
    Date.now() - held.sinceMs < HOLD_PREVIOUS_VISIT_MS;

  useEffect(() => {
    if (!holding || !held) return;
    const timer = setTimeout(repaint, held.sinceMs + HOLD_PREVIOUS_VISIT_MS - Date.now() + 50);
    return () => clearTimeout(timer);
  }, [holding]);

  // Growing old is an event nothing else announces. A ranking that was nine
  // minutes old when the screen opened is due one minute later, and a phone left
  // face-up on the towel renders nothing in between: without this, a screen open
  // and untouched would never refresh at all — there is no polling anywhere.
  useEffect(() => {
    if (updatedMs == null || shouldRevalidate) return;
    // `actualizadoMs` and not the age as a dependency: the age changes on every
    // render, so the wait would start over each time anything else repainted.
    const missing = RANKING_REVALIDATE_AGE_MS - (Date.now() - updatedMs);
    const timer = setTimeout(repaint, Math.max(missing, 0) + 1000);
    return () => clearTimeout(timer);
  }, [updatedMs, shouldRevalidate]);

  // Automatic refetch, once a minute for as long as the ranking stays old: the
  // response the service worker gave up on may never arrive (backend asleep,
  // bad network, or the same old ranking again). It used to be ONE attempt per
  // ranking, and on 9-oct-2026 a phone tab resumed after three hours spent it in
  // the second the radio was still waking up: the worker served its stored
  // copy, the attempt was gone, and the screen sat on "actualizado hace 3h"
  // with the backend answering fine. It hangs on `shouldRevalidate` and NOT on
  // the notice: the day the two thresholds were one, raising the notice to
  // something honest would have silently turned this into an hourly refresh.
  useEffect(() => {
    if (!shouldRevalidate) return;
    const tick = () => {
      if (Date.now() - lastAutoRetryMs < AUTO_RETRY_EVERY_MS) return;
      lastAutoRetryMs = Date.now();
      void retry();
    };
    tick();
    const timer = setInterval(tick, AUTO_RETRY_EVERY_MS);
    return () => clearInterval(timer);
  }, [shouldRevalidate, retry]);

  if (holding) {
    return {
      ranking: null,
      updatedMs: null,
      fromPreviousVisit: false,
      loading: true,
      refreshing: true,
      error: false,
      retrying,
      retry,
    };
  }

  return {
    ranking,
    updatedMs,
    fromPreviousVisit,
    loading: ranking == null && !error,
    refreshing: false,
    error,
    retrying,
    retry,
  };
}
